import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  DEVICE_DATE_RANGE_FILTERS,
  deviceDateFilters,
  deviceFilterParams,
  useDeleteDevice,
  useDevices,
  useDeviceTasks,
  useDeviceTotal,
  useUpdateDevice,
  type DeviceFilters,
  type DeviceSearchField,
} from "../../api/devices";
import type { DayRange } from "../../lib/dayRange";
import { useCsvExport } from "../../api/exports";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { formatRange, totalPagesFor, useCursorPaging } from "../../hooks/useCursorPaging";
import { useDeviceModels } from "../../api/deviceTypes";
import { deviceModelLabel } from "../../api/deviceModelLabel";
import { useAuditLogPages } from "../../api/audit";
import { useGroups } from "../../api/groups";
import { DataTable } from "../../components/DataTable";
import { StatusBadge } from "../../components/StatusBadge";
import type { Device } from "../../api/types";
import { getRole, hasMinRole } from "../../auth/session";
import { Copy, ChevronDown, Download, Move as MoveIcon, Upload, Trash2, RefreshCw, Rocket, Settings2, Pencil } from "lucide-react";

// The statuses the list shows (derived from last-seen time), so the only
// ones a filter can match.
const statusOptions = [
  { value: "online", label: "Online" },
  { value: "stale", label: "Stale" },
  { value: "offline", label: "Offline" },
];

const searchFields = [
  { value: "imei", label: "IMEI" },
  { value: "deviceName", label: "Name" },
  { value: "contactNumber", label: "Contact" },
  { value: "model", label: "Model" },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const SEARCH_DEBOUNCE_MS = 300;
export function DeviceListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState<DeviceSearchField>("imei");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateRanges, setDateRanges] = useState<Record<string, DayRange>>({});
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [selectedImeis, setSelectedImeis] = useState<string[]>([]);
  const [moreActionsOpen, setMoreActionsOpen] = useState(false);
  const moreActionsRef = useRef<HTMLDivElement>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const moveRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"details" | "tasks" | "changes">("details");
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);

  const deleteDevice = useDeleteDevice();
  const updateDevice = useUpdateDevice();
  const deviceModels = useDeviceModels().data?.data ?? [];
  const canAddDevice = getRole() === "super_admin";
  const canMoveDevices = hasMinRole("tenant_admin");
  const groupsQuery = useGroups({ counts: false }, { enabled: canMoveDevices });
  const groups = useMemo(
    () => [...(groupsQuery.data?.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [groupsQuery.data?.data],
  );
  // Exports are an admin action (API Design §4.11); the server enforces it.
  const canExport = hasMinRole("tenant_admin");

  function showFlash(message: string) {
    setFlash(message);
    setTimeout(() => setFlash(null), 3500);
  }

  async function copyImei(imei: string) {
    try {
      await navigator.clipboard.writeText(imei);
      showFlash("IMEI copied to clipboard.");
    } catch {
      showFlash("Could not copy IMEI.");
    }
  }

  // The server writes the file from the same filters as the list, across
  // every matching device however many there are, then the browser
  // downloads it.
  const csvExport = useCsvExport("devices", {
    onDone: (job) => showFlash(`Exported ${job.rowCount.toLocaleString()} device${job.rowCount === 1 ? "" : "s"} to CSV.`),
    onError: showFlash,
  });
  const exportProgress = csvExport.running
    ? `Preparing CSV export…${csvExport.rowCount > 0 ? ` ${csvExport.rowCount.toLocaleString()} devices so far` : ""}`
    : null;

  // Reset the detail panel back to Device details whenever a new
  // device is selected; otherwise the previously-chosen tab leaks
  // across rows which is confusing.
  useEffect(() => {
    setActiveTab("details");
    setActionsOpen(false);
  }, [selectedDevice?.imei]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (actionsRef.current && !actionsRef.current.contains(e.target as Node)) {
        setActionsOpen(false);
      }
    }
    if (actionsOpen) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [actionsOpen]);

  function goToAddDevice() {
    navigate("/devices/new");
  }

  function goToCreateTask(imeis: string[]) {
    if (imeis.length === 0) {
      showFlash("Select one or more devices first.");
      return;
    }
    navigate(`/tasks/create?imeis=${encodeURIComponent(imeis.join(","))}`);
  }

  function goToUploadFile(imeis: string[]) {
    if (imeis.length === 0) {
      showFlash("Select one or more devices first.");
      return;
    }
    navigate(`/firmware/upload?imeis=${encodeURIComponent(imeis.join(","))}`);
  }

  function goToShadowSettings(imei: string) {
    navigate(`/devices/${encodeURIComponent(imei)}/shadow-settings`);
  }

  // Opens the device detail page straight in edit mode (name, group, type,
  // model, description). IMEI stays read-only and there's no company/tenant
  // field — a tenant can't change either.
  function goToEditDevice(imei: string) {
    navigate(`/devices/${encodeURIComponent(imei)}`, { state: { edit: true } });
  }

  function goToSelectedShadowSettings() {
    if (selectedImeis.length !== 1) {
      showFlash("Select exactly one device to edit shadow settings.");
      return;
    }
    goToShadowSettings(selectedImeis[0]);
  }

  async function moveSelectedDevices(groupId: string | null, groupLabel: string) {
    if (selectedImeis.length === 0) return;
    setMoveOpen(false);
    try {
      await Promise.all(
        selectedImeis.map((imei) =>
          updateDevice.mutateAsync({
            imei,
            body: { groupId },
          }),
        ),
      );
      showFlash(`Moved ${selectedImeis.length} device${selectedImeis.length === 1 ? "" : "s"} to ${groupLabel}.`);
      setSelectedImeis([]);
    } catch {
      showFlash("Move failed — see console for details.");
    }
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreActionsRef.current && !moreActionsRef.current.contains(e.target as Node)) {
        setMoreActionsOpen(false);
      }
    }
    if (moreActionsOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [moreActionsOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moveRef.current && !moveRef.current.contains(e.target as Node)) {
        setMoveOpen(false);
      }
    }
    if (moveOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [moveOpen]);

  // Search, status, paging and the total all run on the server, so a match
  // on any page is found and the count is the fleet's, not the page's.
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const filters: DeviceFilters = {
    search: debouncedSearch,
    searchField,
    status: statusFilter || undefined,
    ...deviceDateFilters(dateRanges),
  };
  const paging = useCursorPaging(JSON.stringify([deviceFilterParams(filters).toString(), pageSize]));
  const { data, isLoading, isPlaceholderData } = useDevices({ ...filters, limit: pageSize, cursor: paging.cursor });
  const totalQuery = useDeviceTotal(filters);
  const rows = useMemo(() => data?.data ?? [], [data?.data]);
  const hasMore = !!data?.pagination.hasMore;
  const total = totalQuery.isPlaceholderData ? undefined : totalQuery.data?.total;
  const totalIsLowerBound = !totalQuery.isPlaceholderData && !!totalQuery.data?.isLowerBound;

  // Selection belongs to the page on screen, so bulk actions never act on
  // rows the user can no longer see.
  function showNextPage() {
    const next = data?.pagination.cursor;
    if (!next || isPlaceholderData) return;
    setSelectedImeis([]);
    paging.next(next, rows.length);
  }

  // Keep the selection to rows on screen: while a new search loads, the old
  // rows stay visible (and selectable), so drop anything that didn't survive
  // into the new results.
  useEffect(() => {
    if (isPlaceholderData) return;
    const visible = new Set(rows.map((d) => d.imei));
    setSelectedImeis((current) => {
      const kept = current.filter((imei) => visible.has(imei));
      return kept.length === current.length ? current : kept;
    });
  }, [rows, isPlaceholderData]);

  const columns = [
    { key: "imei", header: "IMEI", sortable: true },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (row: Device) => <StatusBadge status={row.status} variant="dot" />,
    },
    { key: "queue", header: "Queue", render: () => <span className="text-gray-400 dark:text-riot-navy-400">Empty</span> },
    { key: "description", header: "Description", render: () => <span className="text-gray-400 dark:text-riot-navy-400">-</span> },
    { key: "firmwareVersion", header: "Firmware", sortable: true, render: (row: Device) => row.firmwareVersion || <span className="text-gray-400 dark:text-riot-navy-400">-</span> },
    { key: "configId", header: "Configuration", sortable: true, render: () => <span className="text-gray-400 dark:text-riot-navy-400">-</span> },
    { key: "model", header: "Model", sortable: true, render: (row: Device) => deviceModelLabel(row.model, deviceModels) || <span className="text-gray-400 dark:text-riot-navy-400">-</span> },
    {
      key: "groupId",
      header: "Group",
      sortable: true,
      render: (row: Device) => {
        const group = groups.find((candidate) => candidate.groupId === row.groupId);
        return row.groupId ? group?.name ?? row.groupId : <span className="text-gray-400 dark:text-riot-navy-400">—</span>;
      },
    },
    {
      key: "lastSeen",
      header: "Seen at",
      sortable: true,
      render: (row: Device) =>
        row.lastSeen ? new Date(row.lastSeen).toLocaleString() : <span className="text-gray-400 dark:text-riot-navy-400">-</span>,
    },
    {
      key: "shadowSettings",
      header: "Shadow setting",
      width: "150px",
      render: (row: Device) => (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            goToShadowSettings(row.imei);
          }}
          className="flex items-center gap-1 rounded-md border border-riot-gold-500/60 px-2.5 py-1 text-xs font-medium text-riot-gold-500 hover:bg-riot-gold-500/10"
        >
          <Settings2 size={13} /> Shadow setting
        </button>
      ),
    },
  ];

  return (
    <div className={`flex flex-col ${selectedDevice ? "h-full" : "h-auto"}`}>
      {/* Hidden file input powering the "Import devices" dropdown item.
          Reports a mock summary so UX flow is reviewable without a real
          backend parser in place. */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          showFlash(`Import queued: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
          e.target.value = "";
        }}
      />
      {(flash ?? exportProgress) && (
        <div
          role="status"
          className="fixed bottom-6 right-6 z-50 rounded-md bg-riot-navy-900 px-4 py-2 text-xs text-white shadow-lg dark:bg-riot-gold-400 dark:text-riot-navy-900"
        >
          {flash ?? exportProgress}
        </div>
      )}
      <div className={selectedDevice ? "flex-1 overflow-auto" : ""}>
        {isLoading ? (
          <div className="py-20 text-center text-sm text-gray-400 dark:text-riot-navy-400">Loading devices...</div>
        ) : (
          <DataTable
            title="Devices"
            totalCount={total}
            columns={columns}
            data={rows}
            keyExtractor={(d) => d.imei}
            onRowClick={(d) => setSelectedDevice(d)}
            selectable
            selectedKeys={selectedImeis}
            onSelectionChange={setSelectedImeis}
            serverSide
            emptyMessage={
              hasMore && rows.length === 0 && !isPlaceholderData ? (
                <span>
                  No matches in the devices checked so far.{" "}
                  <button type="button" onClick={showNextPage} className="font-medium text-riot-gold-600 hover:text-riot-gold-700 dark:text-riot-gold-400">
                    Keep searching
                  </button>
                </span>
              ) : undefined
            }
            filters={{
              searchPlaceholder: "Search by",
              searchFields,
              statusOptions,
              dateRangeFilters: DEVICE_DATE_RANGE_FILTERS,
            }}
            onFilterChange={(f) => {
              setSelectedImeis([]);
              setSearch(f.search);
              setSearchField(f.searchField as DeviceSearchField);
              setStatusFilter(f.status);
              setDateRanges(f.dateRanges);
            }}
            pagination={{
              page: paging.page,
              totalPages: totalPagesFor(paging.page, pageSize, hasMore, total),
              count: rows.length,
              hasMore,
              rangeLabel: formatRange(paging.offset, rows.length, total, totalIsLowerBound),
              pageSize,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              onPageSizeChange: (size) => {
                setSelectedImeis([]);
                setPageSize(size);
              },
              onNext: showNextPage,
              onPrev: () => {
                setSelectedImeis([]);
                paging.prev();
              },
              onFirst: () => {
                setSelectedImeis([]);
                paging.first();
              },
            }}
            actions={
              <>
                {canAddDevice && (
                  <button
                    onClick={goToAddDevice}
                    className="rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600"
                  >
                    Add device
                  </button>
                )}
                <button
                  onClick={goToSelectedShadowSettings}
                  disabled={selectedImeis.length !== 1}
                  className="flex items-center gap-1 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:cursor-not-allowed disabled:opacity-45"
                  title={selectedImeis.length === 1 ? "Edit shadow settings for selected device" : "Select exactly one device first"}
                >
                  <Settings2 size={13} /> Shadow setting
                </button>
                <button
                  onClick={() => goToUploadFile(selectedImeis)}
                  disabled={selectedImeis.length === 0}
                  className="flex items-center gap-1 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:cursor-not-allowed disabled:opacity-45"
                  title={selectedImeis.length === 0 ? "Select one or more devices first" : "Upload a firmware file for selected devices"}
                >
                  <Upload size={13} /> Add file{selectedImeis.length > 0 ? ` (${selectedImeis.length})` : ""}
                </button>
                <button
                  onClick={() => goToCreateTask(selectedImeis)}
                  disabled={selectedImeis.length === 0}
                  className="flex items-center gap-1 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:cursor-not-allowed disabled:opacity-45"
                  title={selectedImeis.length === 0 ? "Select one or more devices first" : "Create firmware task for selected devices"}
                >
                  <Rocket size={13} /> Create task{selectedImeis.length > 0 ? ` (${selectedImeis.length})` : ""}
                </button>
                {canMoveDevices && (
                  <div className="relative" ref={moveRef}>
                    <button
                      type="button"
                      onClick={() => setMoveOpen((value) => !value)}
                      disabled={selectedImeis.length === 0 || updateDevice.isPending}
                      className={`flex items-center gap-1 rounded-md border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                        selectedImeis.length === 0 || updateDevice.isPending
                          ? "cursor-not-allowed border-gray-200 text-gray-400 dark:border-riot-navy-700 dark:text-riot-navy-500"
                          : moveOpen
                            ? "border-riot-gold-400 bg-riot-gold-50 text-riot-navy-900 dark:border-riot-gold-500 dark:bg-riot-navy-700 dark:text-riot-gold-400"
                            : "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                      }`}
                      title={selectedImeis.length === 0 ? "Select one or more devices first" : "Move selected devices to a group"}
                    >
                      <MoveIcon size={12} />
                      {updateDevice.isPending ? "Moving…" : `Move${selectedImeis.length > 0 ? ` (${selectedImeis.length})` : ""}`}
                      <ChevronDown size={12} className={moveOpen ? "rotate-180 transition-transform" : "transition-transform"} />
                    </button>
                    {moveOpen && (
                      <div className="absolute right-0 top-full z-10 mt-1 w-56 rounded-md border border-gray-200 bg-white py-2 shadow-lg dark:border-riot-navy-700 dark:bg-riot-navy-800">
                        <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-riot-navy-400">
                          Move to group
                        </div>
                        {groups.length === 0 ? (
                          <div className="px-3 py-1.5 text-xs text-gray-400 dark:text-riot-navy-400">
                            {groupsQuery.isLoading ? "Loading groups…" : "No groups available."}
                          </div>
                        ) : (
                          groups.map((group) => (
                            <button
                              key={group.groupId}
                              type="button"
                              onClick={() => void moveSelectedDevices(group.groupId, group.name)}
                              className="block w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                            >
                              {group.name}
                              <span className="ml-1.5 text-[10px] text-gray-400 dark:text-riot-navy-400">({group.type})</span>
                            </button>
                          ))
                        )}
                        <button
                          type="button"
                          onClick={() => void moveSelectedDevices(null, "no group")}
                          className="block w-full px-3 py-1.5 text-left text-xs text-gray-500 hover:bg-gray-50 dark:text-riot-navy-300 dark:hover:bg-riot-navy-700"
                        >
                          Clear group assignment
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <div className="relative" ref={moreActionsRef}>
                  <button
                    onClick={() => setMoreActionsOpen((v) => !v)}
                    className={`flex items-center gap-1 rounded-md border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                      moreActionsOpen
                        ? "border-riot-gold-400 bg-riot-gold-50 text-riot-navy-900 dark:border-riot-gold-500 dark:bg-riot-navy-700 dark:text-riot-gold-400"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                    }`}
                  >
                    More actions <ChevronDown size={12} className={moreActionsOpen ? "rotate-180 transition-transform" : "transition-transform"} />
                  </button>
                  {moreActionsOpen && (
                    <div className="absolute right-0 top-full z-10 mt-1 w-48 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-riot-navy-700 dark:bg-riot-navy-800">
                      <button
                        onClick={() => { setMoreActionsOpen(false); fileInputRef.current?.click(); }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                      >
                        <Upload size={13} /> Import devices
                      </button>
                      {canExport && (
                        <button
                          disabled={csvExport.running}
                          onClick={() => {
                            setMoreActionsOpen(false);
                            csvExport.start(deviceFilterParams(filters));
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-45 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                        >
                          <Download size={13} /> {csvExport.running ? "Exporting…" : "Export to CSV"}
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setMoreActionsOpen(false);
                          const count = selectedImeis.length || (data?.data.length ?? 0);
                          showFlash(`Shadow refresh queued for ${count} device${count === 1 ? "" : "s"}.`);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                      >
                        <RefreshCw size={13} /> Refresh shadows
                      </button>
                      <button
                        onClick={() => {
                          setMoreActionsOpen(false);
                          goToUploadFile(selectedImeis);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                      >
                        <Upload size={13} /> Add firmware file
                      </button>
                      <button
                        onClick={() => {
                          setMoreActionsOpen(false);
                          goToCreateTask(selectedImeis);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                      >
                        <Rocket size={13} /> Create firmware task
                      </button>
                      <div className="my-1 border-t border-gray-100 dark:border-riot-navy-700" />
                      <button
                        onClick={() => {
                          setMoreActionsOpen(false);
                          if (selectedImeis.length === 0) {
                            showFlash("No devices selected.");
                            return;
                          }
                          showFlash(`${selectedImeis.length} device${selectedImeis.length === 1 ? "" : "s"} queued for deletion.`);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                      >
                        <Trash2 size={13} /> Delete selected
                      </button>
                    </div>
                  )}
                </div>
              </>
            }
          />
        )}
      </div>

      {/* Slide-up device detail panel */}
      {selectedDevice && (
        <div className="border-t-2 border-gray-300 bg-white shadow-[0_-18px_36px_rgba(15,23,42,0.08)] dark:border-riot-navy-700 dark:bg-riot-navy-900/95 dark:shadow-[0_-20px_48px_rgba(0,0,0,0.45)]">
          {/* Drag handle */}
          <div className="flex justify-center py-1">
            <div className="h-1 w-8 rounded-full bg-gray-300 dark:bg-riot-navy-600" />
          </div>

          {/* Device header */}
          <div className="flex items-center justify-between px-5 pb-2">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gray-100 text-gray-400 dark:bg-riot-navy-700 dark:text-riot-navy-400">
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                  <rect x="3" y="2" width="14" height="16" rx="2" stroke="currentColor" fill="none" strokeWidth="1.5" />
                </svg>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">IMEI: {selectedDevice.imei}</span>
                  <button
                    onClick={() => void copyImei(selectedDevice.imei)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-riot-gold-400"
                    aria-label="Copy IMEI"
                  >
                    <Copy size={13} />
                  </button>
                </div>
                <p className="text-xs text-gray-400 dark:text-riot-navy-400">
                  Model: {deviceModelLabel(selectedDevice.model, deviceModels) || "Unknown"} &middot; {selectedDevice.deviceName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => goToEditDevice(selectedDevice.imei)}
                className="flex items-center gap-1 rounded-md border border-riot-gold-500/60 px-3 py-1.5 text-xs font-medium text-riot-gold-600 hover:bg-riot-gold-500/10 dark:text-riot-gold-400"
              >
                <Pencil size={13} /> Edit
              </button>
              <button
                onClick={() => goToShadowSettings(selectedDevice.imei)}
                className="flex items-center gap-1 rounded-md bg-riot-gold-400 px-3 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600"
              >
                <Settings2 size={13} /> Shadow setting
              </button>
              <DeviceActionsMenu
                containerRef={actionsRef}
                open={actionsOpen}
                onToggle={() => setActionsOpen((v) => !v)}
                onClose={() => setActionsOpen(false)}
                onCopy={() => void copyImei(selectedDevice.imei)}
                onEdit={() => goToEditDevice(selectedDevice.imei)}
                onRefresh={() => showFlash(`Shadow refresh queued for ${selectedDevice.deviceName}.`)}
                onShadowSettings={() => goToShadowSettings(selectedDevice.imei)}
                onUploadFile={() => goToUploadFile([selectedDevice.imei])}
                onCreateTask={() => goToCreateTask([selectedDevice.imei])}
                onDelete={async () => {
                  if (!window.confirm(`Delete ${selectedDevice.deviceName}? This cannot be undone.`)) return;
                  try {
                    await deleteDevice.mutateAsync(selectedDevice.imei);
                    showFlash(`Deleted ${selectedDevice.deviceName}.`);
                    setSelectedDevice(null);
                  } catch {
                    showFlash("Delete failed — see console for details.");
                  }
                }}
              />
              <button
                onClick={() => setSelectedDevice(null)}
                className="rounded-md px-2 py-1 text-xs text-gray-400 hover:bg-gray-100 dark:text-riot-navy-300 dark:hover:bg-riot-navy-700 dark:hover:text-riot-navy-100"
              >
                &times;
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 px-5 dark:border-riot-navy-700">
            {([
              { id: "details", label: "Device details" },
              { id: "tasks", label: "Tasks" },
              { id: "changes", label: "Changes" },
            ] as const).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`border-b-2 px-4 py-2 text-xs font-medium transition-colors ${
                  activeTab === tab.id
                    ? "border-riot-gold-500 text-riot-gold-500"
                    : "border-transparent text-gray-500 hover:text-gray-700 dark:text-riot-navy-200 dark:hover:text-white"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {activeTab === "details" && (
            <div className="grid grid-cols-1 gap-6 px-5 py-4 sm:grid-cols-3">
              <div>
                <h4 className="mb-2 flex items-center gap-1 text-xs font-semibold text-gray-900 dark:text-white">
                  General
                  <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-riot-gold-100 text-[8px] font-bold text-riot-gold-500">i</span>
                </h4>
                <dl className="space-y-1.5 text-xs">
                  <div className="flex"><dt className="w-24 text-gray-400 dark:text-riot-navy-400">Group:</dt><dd className="text-gray-700 dark:text-riot-navy-200">{selectedDevice.groupId || "-"}</dd></div>
                  <div className="flex"><dt className="w-24 text-gray-400 dark:text-riot-navy-400">Status:</dt><dd><StatusBadge status={selectedDevice.status} variant="dot" /></dd></div>
                  <div className="flex"><dt className="w-24 text-gray-400 dark:text-riot-navy-400">Firmware:</dt><dd className="text-gray-700 dark:text-riot-navy-200">{selectedDevice.firmwareVersion || "-"}</dd></div>
                  <div className="flex"><dt className="w-24 text-gray-400 dark:text-riot-navy-400">Hardware:</dt><dd className="text-gray-700 dark:text-riot-navy-200">{selectedDevice.hardwareVersion || "-"}</dd></div>
                  <div className="flex"><dt className="w-24 text-gray-400 dark:text-riot-navy-400">Registered:</dt><dd className="text-gray-700 dark:text-riot-navy-200">{new Date(selectedDevice.createdAt).toLocaleString()}</dd></div>
                  <div className="flex"><dt className="w-24 text-gray-400 dark:text-riot-navy-400">Last seen:</dt><dd className="text-gray-700 dark:text-riot-navy-200">{selectedDevice.lastSeen ? new Date(selectedDevice.lastSeen).toLocaleString() : "-"}</dd></div>
                </dl>
              </div>
            </div>
          )}
          {activeTab === "tasks" && <DeviceTasksTab imei={selectedDevice.imei} />}
          {activeTab === "changes" && <DeviceChangesTab imei={selectedDevice.imei} />}
        </div>
      )}
    </div>
  );
}

// --- Device detail panel: Tasks tab ---

function DeviceTasksTab({ imei }: { imei: string }) {
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useDeviceTasks(imei);
  const tasks = data?.pages.flatMap((page) => page.data) ?? [];
  const loadOlder = hasNextPage ? (
    <button
      type="button"
      onClick={() => void fetchNextPage()}
      disabled={isFetchingNextPage}
      className="mt-2 text-xs font-medium text-riot-gold-600 hover:text-riot-gold-700 disabled:opacity-50 dark:text-riot-gold-400"
    >
      {isFetchingNextPage ? "Loading…" : tasks.length === 0 ? "Keep searching" : "Load older tasks"}
    </button>
  ) : null;

  if (isLoading) {
    return <div className="px-5 py-6 text-xs text-gray-400 dark:text-riot-navy-400">Loading tasks...</div>;
  }
  if (tasks.length === 0) {
    // The server can stop at its work budget before it finds one of this
    // device's tasks: that's "more to check", not "none".
    return (
      <div className="px-5 py-6 text-xs text-gray-400 dark:text-riot-navy-400">
        {hasNextPage ? "No tasks found for this device in the tasks checked so far." : "No tasks have touched this device."}
        {loadOlder && <div>{loadOlder}</div>}
      </div>
    );
  }
  return (
    <div className="px-5 py-3">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-gray-200 text-gray-500 dark:border-riot-navy-700 dark:text-riot-navy-400">
            <th className="py-2 font-medium">Task</th>
            <th className="py-2 font-medium">Device status</th>
            <th className="py-2 font-medium">Strategy</th>
            <th className="py-2 font-medium">Firmware</th>
            <th className="py-2 font-medium">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-riot-navy-700">
          {tasks.map((t) => (
            <tr key={t.taskId} className="text-gray-700 dark:text-riot-navy-200">
              <td className="py-2">{t.name}</td>
              <td className="py-2"><StatusBadge status={t.deviceStatus} /></td>
              <td className="py-2 capitalize">{t.rolloutStrategy}</td>
              <td className="py-2 font-mono text-[11px]">{t.firmwareId || "-"}</td>
              <td className="py-2">{new Date(t.createdAt).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {loadOlder}
    </div>
  );
}

// --- Device detail panel: Changes tab ---

function DeviceChangesTab({ imei }: { imei: string }) {
  // This device's own audit trail (server-filtered by entityId), newest
  // first, a page at a time.
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useAuditLogPages({ entityId: imei, limit: 50 });
  const entries = data?.pages.flatMap((page) => page.data) ?? [];

  if (isLoading) {
    return <div className="px-5 py-6 text-xs text-gray-400 dark:text-riot-navy-400">Loading changes...</div>;
  }
  if (entries.length === 0) {
    // The server can use up its per-request budget before it reaches this
    // device's events; with more to check, that isn't "no history".
    if (hasNextPage) {
      return (
        <div className="px-5 py-6 text-xs text-gray-400 dark:text-riot-navy-400">
          No changes found in the events checked so far.{" "}
          <button
            type="button"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="font-medium text-riot-gold-600 hover:text-riot-gold-700 disabled:opacity-50 dark:text-riot-gold-400"
          >
            {isFetchingNextPage ? "Searching…" : "Keep searching"}
          </button>
        </div>
      );
    }
    return <div className="px-5 py-6 text-xs text-gray-400 dark:text-riot-navy-400">No audit events for this device yet.</div>;
  }
  return (
    <div className="px-5 py-3">
      <ul className="space-y-2">
        {entries.map((e) => (
          <li key={e.id} className="flex items-start gap-3 rounded-md border border-gray-100 p-2 dark:border-riot-navy-700">
            <div className="pt-0.5">
              <StatusBadge status={e.action} />
            </div>
            <div className="flex-1 text-xs">
              <div className="text-gray-800 dark:text-riot-navy-100">{e.details || `${e.action} on ${e.entityType}`}</div>
              <div className="mt-0.5 text-[11px] text-gray-400 dark:text-riot-navy-400">
                {e.userId} · {new Date(e.timestamp).toLocaleString()}
              </div>
            </div>
          </li>
        ))}
      </ul>
      {hasNextPage && (
        <button
          type="button"
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
          className="mt-3 text-xs font-medium text-riot-gold-600 hover:text-riot-gold-700 disabled:opacity-50 dark:text-riot-gold-400"
        >
          {isFetchingNextPage ? "Loading…" : "Load older changes"}
        </button>
      )}
    </div>
  );
}

// --- Device detail panel: Actions dropdown ---

interface DeviceActionsMenuProps {
  containerRef: React.RefObject<HTMLDivElement>;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onCopy: () => void;
  onEdit: () => void;
  onRefresh: () => void;
  onShadowSettings: () => void;
  onUploadFile: () => void;
  onCreateTask: () => void;
  onDelete: () => void;
}

function DeviceActionsMenu({ containerRef, open, onToggle, onClose, onCopy, onEdit, onRefresh, onShadowSettings, onUploadFile, onCreateTask, onDelete }: DeviceActionsMenuProps) {
  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={onToggle}
        className={`flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs transition-colors ${
          open
            ? "border-riot-gold-400 bg-riot-gold-50 text-riot-navy-900 dark:border-riot-gold-500 dark:bg-riot-navy-700 dark:text-riot-gold-400"
            : "border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
        }`}
      >
        Actions <ChevronDown size={12} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-10 mt-1 w-48 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-riot-navy-700 dark:bg-riot-navy-800">
          <button
            onClick={() => { onClose(); onEdit(); }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
          >
            <Pencil size={13} /> Edit device
          </button>
          <button
            onClick={() => { onClose(); onCopy(); }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
          >
            <Copy size={13} /> Copy IMEI
          </button>
          <button
            onClick={() => { onClose(); onRefresh(); }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
          >
            <RefreshCw size={13} /> Refresh shadow
          </button>
          <button
            onClick={() => { onClose(); onShadowSettings(); }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
          >
            <Settings2 size={13} /> Shadow setting
          </button>
          <button
            onClick={() => { onClose(); onUploadFile(); }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
          >
            <Upload size={13} /> Add firmware file
          </button>
          <button
            onClick={() => { onClose(); onCreateTask(); }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
          >
            <Rocket size={13} /> Create firmware task
          </button>
          <div className="my-1 border-t border-gray-100 dark:border-riot-navy-700" />
          <button
            onClick={() => { onClose(); onDelete(); }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
          >
            <Trash2 size={13} /> Delete device
          </button>
        </div>
      )}
    </div>
  );
}
